/**
 * Athlete Season Analytics — V1.0
 *
 * Single scrollable page bound to:
 *   GET /api/season-analytics/athlete/{athlete_id}?start_date=...&end_date=...
 *
 * Sections (in order):
 *   1  Hero KPI strip       (4 cards)
 *   2  Season narrative
 *   3  Availability timeline (stacked horizontal bar per week)
 *   4  Training load        (daily bar + weekly bar)
 *   5  Wellness             (5-series trend)
 *   6  Performance progress (jump · vbt · aerobic)
 *   7  Training distribution (donut + monthly stack)
 *   8  RTP & medical
 *   9  Comparisons          (4 modes)
 */
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  ArrowLeft, Activity, Award, AlertTriangle, Calendar, ChevronRight,
  Download, FileSpreadsheet, Heart, Loader2, Users, Zap,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { LongitudinalProgress } from '../components/reports/LongitudinalProgress';
import { LongitudinalSummary } from '../components/reports/LongitudinalTables';
import { useSeasonReport } from '../hooks/useSeasonReport';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import AthleteMatchExposureSection from '../components/reports/AthleteMatchExposureSection';

const PALETTE = ['#1E6BFF', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

const fmt = (v, suffix = '') => (v === null || v === undefined ? '—' : `${v}${suffix}`);
const fmtPct = (v) => (v === null || v === undefined ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`);

const KpiCard = ({ label, value, hint, accent = 'primary', testid }) => (
  <Card
    className="bg-zinc-900 border-zinc-800 p-4"
    data-testid={testid}
  >
    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">
      {label}
    </div>
    <div
      className={`text-3xl font-bold ${
        accent === 'good' ? 'text-emerald-400' :
        accent === 'warn' ? 'text-amber-400' :
        accent === 'critical' ? 'text-red-400' :
        'text-cyan-300'
      }`}
    >
      {value}
    </div>
    {hint && (
      <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>
    )}
  </Card>
);

const SectionTitle = ({ icon: Icon, title, subtitle, anchor }) => (
  <div className="flex items-start gap-3 mb-3" id={anchor}>
    {Icon && <Icon className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />}
    <div>
      <h2 className="text-lg font-semibold text-zinc-100">{title}</h2>
      {subtitle && (
        <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>
      )}
    </div>
  </div>
);

const NoData = ({ msg }) => (
  <div className="text-xs text-zinc-500 italic py-4">{msg || 'No data available.'}</div>
);

const tooltipStyle = {
  backgroundColor: '#0f172a',
  border: '1px solid #334155',
  borderRadius: '6px',
  fontSize: '11px',
};

// ─── Section components ──────────────────────────────────────────
const AvailabilityChart = ({ weeks }) => {
  if (!weeks?.length) return <NoData />;
  const data = weeks.map(w => ({
    week: w.week_start.slice(5),
    Available: w.available,
    RTP: w.rtp,
    Lost: w.lost,
  }));
  return (
    <ResponsiveContainer width="100%" height={210}>
      <BarChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
        <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
        <XAxis dataKey="week" tick={{ fill: '#94a3b8', fontSize: 9 }} interval="preserveStartEnd" />
        <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
        <Tooltip contentStyle={tooltipStyle} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="Available" stackId="a" fill="#22C55E" />
        <Bar dataKey="RTP" stackId="a" fill="#F59E0B" />
        <Bar dataKey="Lost" stackId="a" fill="#EF4444" />
      </BarChart>
    </ResponsiveContainer>
  );
};

const DailyLoadChart = ({ daily }) => {
  if (!daily?.length) return <NoData />;
  return (
    <ResponsiveContainer width="100%" height={180}>
      <BarChart data={daily} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
        <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
        <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 9 }} interval="preserveStartEnd" />
        <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
        <Tooltip contentStyle={tooltipStyle} />
        <Bar dataKey="load" fill="#1E6BFF" />
      </BarChart>
    </ResponsiveContainer>
  );
};

const WellnessChart = ({ entries }) => {
  if (!entries?.length) return <NoData />;
  return (
    <ResponsiveContainer width="100%" height={210}>
      <LineChart data={entries} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
        <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
        <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 9 }} interval="preserveStartEnd" />
        <YAxis domain={[0, 10]} tick={{ fill: '#94a3b8', fontSize: 10 }} />
        <Tooltip contentStyle={tooltipStyle} />
        <Legend wrapperStyle={{ fontSize: 10 }} />
        {['sleep_quality', 'fatigue', 'soreness', 'stress', 'mood'].map((k, i) => (
          <Line key={k} type="monotone" dataKey={k} stroke={PALETTE[i]} dot={false} strokeWidth={2} name={k.replace('_quality', '').replace('_', ' ')} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

const DistributionDonut = ({ categories }) => {
  if (!categories?.length) return <NoData />;
  const data = categories.filter(c => c.minutes > 0).map(c => ({ name: c.category, value: c.minutes }));
  return (
    <ResponsiveContainer width="100%" height={210}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
          {data.map((d, i) => (
            <Cell key={d.name} fill={d.name.toUpperCase() === 'RTP' ? '#EF4444' : PALETTE[i % PALETTE.length]} />
          ))}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} />
        <Legend wrapperStyle={{ fontSize: 10 }} />
      </PieChart>
    </ResponsiveContainer>
  );
};

const ComparisonRows = ({ rows, testid }) => (
  <div className="space-y-1.5">
    {rows.map((r, i) => (
      <div key={i} className="comparison-metric-row flex items-center justify-between text-xs py-1.5 border-b border-zinc-800/60 last:border-0">
        <span className="text-zinc-400">{r.label}</span>
        <div className="flex items-center gap-3">
          <span className="text-zinc-200 tabular-nums">{fmt(r.athlete)}</span>
          <span className="text-zinc-500 tabular-nums">vs {fmt(r.baseline)}</span>
          <span className="tabular-nums font-semibold w-14 text-right text-zinc-300"
            data-testid={`${testid}-gap-${i}`}>{fmtPct(r.gap_pct)}</span>
        </div>
      </div>
    ))}
  </div>
);

const ComparisonCard = ({ title, mode, testid }) => {
  if (!mode) return null;
  const small = mode.validity === 'SMALL_SAMPLE';
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid={testid}>
      <div className="comparison-heading flex items-center justify-between mb-3">
        <div className="text-xs uppercase text-zinc-500">{title}</div>
        {small && (
          <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-300">
            Small Sample
          </Badge>
        )}
      </div>
      {mode.rows?.length
        ? <ComparisonRows rows={mode.rows} testid={testid} />
        : <NoData msg="No comparable data." />}
    </Card>
  );
};

// ─── Main page ───────────────────────────────────────────────────
const AthleteSeasonAnalyticsPage = () => {
  const { api, user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { athleteId: paramAthleteId } = useParams();
  const t = (en, it) => (language === 'IT' ? it : en);

  // Athletes view their own season; staff use the URL athleteId
  const athleteId = paramAthleteId || user?.athlete_id;

  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const { data, loading, error } = useSeasonReport({ api,
    path: athleteId ? `/season-analytics/athlete/${athleteId}` : null,
    start, end, setStart, setEnd, language });
  const [exporting, setExporting] = useState(null);

  // Export PDF
  const handleExportPdf = async () => {
    if (!athleteId || exporting) return;
    setExporting('pdf');
    try {
      const res = await api.post(
        '/premium-reports/athlete-season',
        { athlete_id: athleteId, start_date: start, end_date: end, language },
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `season_report_${athleteId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('Season PDF generated', 'PDF di stagione generato'));
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
          || t('Failed to export PDF', 'Esportazione PDF fallita'),
      );
    } finally {
      setExporting(null);
    }
  };

  const handleExportXlsx = async () => {
    if (!athleteId || exporting) return;
    setExporting('xlsx');
    try {
      const url = `/season-analytics/athlete/${athleteId}/excel` +
        `?language=${language}` + (start && end ? `&start_date=${start}&end_date=${end}` : '');
      const res = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data]);
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u;
      a.download = `athlete_season_${athleteId.slice(0, 8)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(u);
      toast.success(t('Excel exported', 'Excel esportato'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally {
      setExporting(null);
    }
  };

  const fullName = useMemo(() => {
    if (!data?.athlete) return '';
    return `${data.athlete.first_name || ''} ${data.athlete.last_name || ''}`.trim();
  }, [data]);

  if (!athleteId) return <div className="p-6 text-zinc-300">No athlete selected.</div>;
  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-24 text-zinc-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('Loading…', 'Caricamento…')}
      </div>
    );
  }
  if (error && !data) return <div className="p-6 text-red-400" data-testid="athlete-season-load-error">{error}</div>;
  if (!data) return null;

  const { kpis, availability, load, wellness, performance, training_distribution: td, rtp, comparisons, narrative, findings, recommendations } = data;
  const bp = kpis.best_performance || {};

  return (
    <div className="season-report space-y-6 fade-in" data-testid="athlete-season-analytics-page" aria-busy={loading}>
      {error && <p role="alert" className="text-red-300" data-testid="athlete-season-filter-error">{error}</p>}
      {loading && <p role="status" className="text-zinc-400" data-testid="athlete-season-updating">{t('Updating…', 'Aggiornamento…')}</p>}
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          {paramAthleteId && (
            <Button variant="ghost" size="sm" className="text-zinc-400 -ml-2 mb-1"
              onClick={() => navigate(`/staff/roster/${athleteId}`)}>
              <ArrowLeft className="w-4 h-4 mr-1" /> {t('Back to athlete', 'Indietro')}
            </Button>
          )}
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100">
            {t('Season Analytics', 'Analytics di Stagione')}
            {fullName && <span className="text-zinc-400 font-normal"> — {fullName}</span>}
          </h1>
          <p className="text-sm text-zinc-500 mt-1" data-testid="athlete-season-visible-period">
            {data.period.start_date} → {data.period.end_date}
            {data.athlete.primary_position && <span className="mx-2">·</span>}
            {data.athlete.primary_position}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="text-[10px] uppercase text-zinc-500 block">{t('Start', 'Inizio')}</label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)}
              className="bg-zinc-900 border-zinc-700 text-zinc-100 h-9 w-40"
              data-testid="season-start-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-zinc-500 block">{t('End', 'Fine')}</label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
              className="bg-zinc-900 border-zinc-700 text-zinc-100 h-9 w-40"
              data-testid="season-end-input" />
          </div>
          <Button
            onClick={handleExportPdf}
            disabled={!!exporting || loading || !!error}
            className="h-9 bg-cyan-600 hover:bg-cyan-500 text-white"
            data-testid="export-season-pdf-btn"
          >
            {exporting === 'pdf'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Download className="w-4 h-4 mr-2" />}
            {t('Export PDF', 'Esporta PDF')}
          </Button>
          <Button
            onClick={handleExportXlsx}
            disabled={!!exporting || loading || !!error}
            variant="outline"
            className="h-9 border-zinc-700 text-zinc-200 hover:bg-zinc-800"
            data-testid="export-season-xlsx-btn"
          >
            {exporting === 'xlsx'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <FileSpreadsheet className="w-4 h-4 mr-2" />}
            {t('Excel', 'Excel')}
          </Button>
        </div>
      </div>

      {/* 1 — Hero KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="season-kpis">
        <KpiCard label="Availability" value={`${kpis.availability_pct}%`}
          accent={kpis.availability_pct >= 90 ? 'good' : kpis.availability_pct >= 70 ? 'warn' : 'critical'}
          hint={`${availability.totals.available_days} d available`}
          testid="kpi-availability" />
        <KpiCard label="Total Load" value={kpis.total_load.toLocaleString() + ' AU'}
          hint={`${load.session_count} sessions`} testid="kpi-load" />
        <KpiCard label="Wellness" value={`${kpis.wellness_index}/10`}
          accent={kpis.wellness_index >= 7 ? 'good' : kpis.wellness_index ? 'warn' : 'primary'}
          hint={`${wellness.red_flags} red flag${wellness.red_flags === 1 ? '' : 's'}`}
          testid="kpi-wellness" />
        <KpiCard label={bp.label} value={bp.value !== null ? `${bp.value}${bp.unit || ''}` : '—'}
          accent="primary" testid="kpi-best-perf" />
      </div>

      {/* 2 — Narrative */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="season-narrative">
        <SectionTitle icon={Award} title={t('Season Summary', 'Riassunto di Stagione')}
          subtitle={t('Auto-generated · deterministic', 'Generato automaticamente · deterministico')} />
        <p className="text-sm text-zinc-300 leading-relaxed">{narrative}</p>
      </Card>

      {/* 3 — Availability Timeline */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="section-availability">
        <SectionTitle icon={Calendar} title={t('Availability Timeline', 'Disponibilità')}
          subtitle={`${availability.totals.available_days} available · ${availability.totals.rtp_days} RTP · ${availability.totals.lost_days} lost`} />
        <AvailabilityChart weeks={availability.weeks} />
      </Card>

      {/* 4 — Training Load */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="section-load">
        <SectionTitle icon={Activity} title={t('Training Load', 'Carico Allenamento')}
          subtitle={`Total ${kpis.total_load.toLocaleString()} AU · Weekly avg ${load.weekly_avg.toLocaleString()} AU`} />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4 text-xs">
          <div><span className="text-zinc-500">Total: </span><span className="text-zinc-100 font-semibold">{kpis.total_load.toLocaleString()}</span></div>
          <div><span className="text-zinc-500">Weekly avg: </span><span className="text-zinc-100 font-semibold">{load.weekly_avg.toLocaleString()}</span></div>
          <div><span className="text-zinc-500">Highest: </span><span className="text-zinc-300 font-semibold">{load.highest_week?.load?.toLocaleString() || '—'}</span></div>
          <div><span className="text-zinc-500">Lowest: </span><span className="text-amber-400 font-semibold">{load.lowest_week?.load?.toLocaleString() || '—'}</span></div>
        </div>
        <DailyLoadChart daily={load.daily} />
      </Card>

      {/* 5 — Wellness */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="section-wellness">
        <SectionTitle icon={Heart} title={t('Wellness', 'Benessere')}
          subtitle={`${wellness.entry_count} entries · ${wellness.red_flags} red flags`} />
        <WellnessChart entries={wellness.entries} />
      </Card>

      {/* 6 — Performance Progress */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="section-progress">
        <SectionTitle icon={Zap} title={t('Performance Progress', 'Progresso Performance')}
          subtitle={t('First · Best · Latest · Δ%', 'Primo · Migliore · Ultimo · Δ%')} />
        <div className="grid grid-cols-1 gap-6">
          <LongitudinalProgress label="Jump" block={performance.jump} testId="athlete-longitudinal-jump" />
          <LongitudinalProgress label="VBT" block={performance.vbt} testId="athlete-longitudinal-vbt" />
          <LongitudinalProgress label="Aerobic" block={performance.aerobic} testId="athlete-longitudinal-aerobic" />
        </div>
        <LongitudinalSummary performance={performance} t={t} testId="athlete-longitudinal-summary" />
      </Card>

      {/* 7 — Training Distribution */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="section-distribution">
        <SectionTitle icon={Users} title={t('Training Distribution', 'Distribuzione Allenamento')}
          subtitle={`${td.rtp_pct}% RTP share`} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DistributionDonut categories={td.categories} />
          <div className="text-xs space-y-2">
            {td.categories.map((c, i) => (
              <div key={c.category} className="flex items-center justify-between border-b border-zinc-800/60 pb-1.5">
                <span className="text-zinc-400 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: c.category === 'RTP' ? '#EF4444' : PALETTE[i % PALETTE.length] }} />
                  {c.category}
                </span>
                <span className="text-zinc-100 tabular-nums">{c.minutes.toLocaleString()} min · {c.percentage}%</span>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* 7b — Match Exposure (P0.5.13A) */}
      <AthleteMatchExposureSection
        api={api}
        athleteId={athleteId}
        teamId={null}
        dateFrom={data.period.start_date}
        dateTo={data.period.end_date}
        t={t}
      />

      {/* 8 — RTP */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="section-rtp">
        <SectionTitle icon={AlertTriangle} title={t('RTP & Medical', 'RTP & Medico')}
          subtitle={`${rtp.injury_count} injuries · ${rtp.days_lost} days lost`} />
        {rtp.active && (
          <div className="bg-red-950/30 border border-red-500/40 rounded p-3 mb-3 text-xs text-red-200">
            <b>ACTIVE:</b> {rtp.active.injury_type || '—'} · since {rtp.active.injury_date}
            {rtp.active.expected_return_date && ` · expected return ${rtp.active.expected_return_date}`}
          </div>
        )}
        {rtp.history?.length ? (
          <div className="text-xs">
            <div className="grid grid-cols-5 text-[10px] uppercase text-zinc-500 pb-2 border-b border-zinc-800">
              <span>Date</span><span>Injury</span><span>Severity</span><span>Days</span><span>Status</span>
            </div>
            {rtp.history.map((h, i) => (
              <div key={i} className="grid grid-cols-5 py-1.5 border-b border-zinc-800/60 last:border-0">
                <span className="text-zinc-300">{h.injury_date}</span>
                <span className="text-zinc-100">{h.injury_type || '—'}</span>
                <span className="text-zinc-400">{(h.severity || '—').toUpperCase()}</span>
                <span className="text-zinc-100">{h.days_lost || '—'}</span>
                <span className={`uppercase font-medium ${
                  (h.status || '').toUpperCase() === 'ACTIVE' ? 'text-red-400' :
                  (h.status || '').toUpperCase() === 'COMPLETED' ? 'text-emerald-400' :
                  'text-zinc-400'
                }`}>{h.status || '—'}</span>
              </div>
            ))}
            {rtp.recurrences?.length > 0 && (
              <div className="text-amber-400 text-xs mt-3">⚠ Recurrences: {rtp.recurrences.join(', ')}</div>
            )}
          </div>
        ) : <NoData msg="No injuries in this season." />}
      </Card>

      {/* 9 — Comparisons */}
      <div data-testid="section-comparisons">
        <SectionTitle icon={ChevronRight} title={t('Comparisons', 'Confronti')}
          subtitle={t('vs previous period · team · position · best teammate',
                     'vs periodo precedente · squadra · ruolo · migliore compagno')} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ComparisonCard title={t('vs Previous Period', 'vs Periodo Precedente')}
            mode={comparisons?.previous_period} testid="cmp-prev" />
          <ComparisonCard title={t('vs Team Average', 'vs Media Squadra')}
            mode={comparisons?.team_average} testid="cmp-team" />
          <ComparisonCard title={t('vs Position Average', 'vs Media Ruolo')}
            mode={comparisons?.position_average} testid="cmp-position" />
          <ComparisonCard title={t('vs Best Teammate', 'vs Migliore Compagno')}
            mode={comparisons?.team_best} testid="cmp-best" />
        </div>
      </div>

      {/* 10 — Findings & Recommendations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="section-findings">
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <SectionTitle icon={Award} title={t('Key Findings', 'Risultati Chiave')} />
          <ul className="space-y-2 text-sm text-zinc-300">
            {findings.map((f, i) => <li key={i}>• {f}</li>)}
          </ul>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <SectionTitle icon={ChevronRight} title={t('Recommendations', 'Raccomandazioni')} />
          <ul className="space-y-2 text-sm text-zinc-300">
            {recommendations.map((r, i) => <li key={i}>• {r}</li>)}
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default AthleteSeasonAnalyticsPage;
